import { api } from './client';

export type BackendContractStatus =
  | 'DRAFT'
  | 'SENT'
  | 'OTP_PENDING'
  | 'SIGNED'
  | 'ACTIVE'
  | 'AMENDED'
  | 'EXPIRED'
  | 'TERMINATED';

export type BackendContractPartyRole = 'HTX' | 'FARMER' | 'AGRIDA';

export type BackendContractBusinessType = 'HTX_FARMER' | 'HTX_BUYER' | 'SUPPLY';

export type BackendEcontractEventType =
  | 'SENT'
  | 'OTP_SENT'
  | 'OTP_VERIFIED'
  | 'SIGNED'
  | 'ACTIVATED'
  | 'AMENDED'
  | 'EXPIRED'
  | 'TERMINATED';

export interface BackendContractParty {
  role: BackendContractPartyRole;
  userId: string;
  // otpCodeHash/otpExpiresAt are cleared server-side once verified — never present after SIGNED.
  otpExpiresAt?: string;
  otpVerifiedAt?: string;
}

export interface BackendContractAttachment {
  fileId: string;
  type: 'image' | 'doc';
  caption?: string;
}

export interface BackendContract {
  _id: string;
  htxId: string;
  farmerId: string;
  templateId: string;
  version: number;
  parties: BackendContractParty[];
  status: BackendContractStatus;
  terms: Record<string, unknown>;
  signedDocFileId?: string;
  supersedes?: string;
  contractNo?: string;
  businessType: BackendContractBusinessType;
  counterpartyName?: string;
  signedDate?: string;
  effectiveDate?: string;
  expiryDate?: string;
  valueVnd?: string;
  expectedUnitPriceVnd?: number;
  mainCrop?: string;
  variety?: string;
  areaHa?: number;
  expectedOutputKg?: number;
  expectedDeliveryDate?: string;
  cropLines?: BackendContractCropLine[];
  seasonIds: string[];
  notes?: string;
  attachments?: BackendContractAttachment[];
  limitGrantedVnd: string;
  limitRemainingVnd?: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown;
}

export interface BackendEcontractEvent {
  _id: string;
  contractId: string;
  event: BackendEcontractEventType;
  actorId: string;
  metadata: Record<string, unknown>;
  timestamp: string;
  [key: string]: unknown;
}

export interface ContractListResult {
  items: BackendContract[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateContractDraftPayload {
  htxId: string;
  farmerId: string;
  templateId: string;
  terms: Record<string, unknown>;
  parties: Array<{ role: BackendContractPartyRole; userId: string }>;
  businessType?: BackendContractBusinessType;
  counterpartyName?: string;
  signedDate?: string;
  effectiveDate?: string;
  expiryDate?: string;
  valueVnd?: number;
  expectedUnitPriceVnd?: number;
  mainCrop?: string;
  variety?: string;
  areaHa?: number;
  expectedOutputKg?: number;
  expectedDeliveryDate?: string;
  cropLines?: BackendContractCropLine[];
  seasonIds?: string[];
  notes?: string;
  attachments?: BackendContractAttachment[];
}

/** One crop of a contract: area (ha), quantity (kg), price (đ/kg). */
export interface BackendContractCropLine {
  crop: string;
  areaHa?: number;
  expectedOutputKg?: number;
  unitPriceVnd?: number;
}

export async function createContractDraft(payload: CreateContractDraftPayload): Promise<BackendContract> {
  return api.post<BackendContract>('/contracts', payload);
}

export interface UpdateContractDraftPayload {
  terms?: Record<string, unknown>;
  counterpartyName?: string;
  expiryDate?: string;
  valueVnd?: number;
  mainCrop?: string;
  variety?: string;
  areaHa?: number;
  expectedOutputKg?: number;
  expectedUnitPriceVnd?: number;
  expectedDeliveryDate?: string;
  notes?: string;
  attachments?: BackendContractAttachment[];
}

export async function updateContractDraft(
  contractId: string,
  payload: UpdateContractDraftPayload,
): Promise<BackendContract> {
  return api.patch<BackendContract>(`/contracts/${contractId}`, payload);
}

// grantAmountVnd is added to the farmer's single running Limit — the backend has no
// per-contract limit ledger, so this never subtracts a prior grant.
export async function grantLimitFromContract(contractId: string, grantAmountVnd: number): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/grant-limit`, { grantAmountVnd });
}

export async function listContractsByHtx(
  htxId: string,
  params?: { status?: BackendContractStatus; page?: number; pageSize?: number },
): Promise<ContractListResult> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  return api.get<ContractListResult>(`/contracts/htx/${htxId}${qs ? `?${qs}` : ''}`);
}

export async function getContractById(contractId: string): Promise<BackendContract> {
  return api.get<BackendContract>(`/contracts/${contractId}`);
}

export async function sendContract(contractId: string): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/send`);
}

export async function requestContractOtp(
  contractId: string,
  partyUserId: string,
): Promise<{ expiresAt: string }> {
  return api.post<{ expiresAt: string }>(`/contracts/${contractId}/otp/request`, { partyUserId });
}

export async function verifyContractOtp(
  contractId: string,
  partyUserId: string,
  code: string,
): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/otp/verify`, { partyUserId, code });
}

/** Farmer-side confirm — no OTP. Being logged into the app already is the
 * verification, so this is a single authenticated tap. Adds the caller as
 * the contract's FARMER party if one wasn't set at draft time. */
export async function confirmContract(contractId: string): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/confirm`);
}

export async function activateContract(contractId: string, signedDocFileId: string): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/activate`, { signedDocFileId });
}

// Amending creates a new DRAFT contract (version + 1) and marks the original AMENDED —
// the returned BackendContract is the new draft, not the original.
export async function amendContract(contractId: string, terms: Record<string, unknown>): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/amend`, { terms });
}

export async function terminateContract(contractId: string, reason?: string): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/terminate`, { reason });
}

export async function listContractsByFarmer(farmerId: string): Promise<BackendContract[]> {
  return api.get<BackendContract[]>(`/contracts/farmers/${farmerId}`);
}

export async function getContractEvents(contractId: string): Promise<BackendEcontractEvent[]> {
  return api.get<BackendEcontractEvent[]>(`/contracts/${contractId}/events`);
}

/** HTX links one of its own, already-existing contracts to a confirmed farmer's
 * season — HTX_STAFF has no contract-creation right, only this linking action. */
export async function addContractAttachment(contractId: string, attachment: BackendContractAttachment): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/attachments`, attachment);
}

export async function linkContractSeason(contractId: string, seasonId: string): Promise<BackendContract> {
  return api.post<BackendContract>(`/contracts/${contractId}/link-season`, { seasonId });
}
